import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from './popover';
import { ChevronDown } from 'lucide-react';
import { format } from "date-fns";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100"
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell:
          "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100"
        ),
        day_range_end: "day-range-end",
        day_selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: "bg-accent text-accent-foreground",
        day_outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ ..._props }) => <ChevronLeft className="h-4 w-4" />,
        IconRight: ({ ..._props }) => <ChevronRight className="h-4 w-4" />,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };

// DateRangePicker for analytics dashboard
import { addDays, startOfMonth, endOfMonth, subDays } from 'date-fns';
// Match DayPicker's DateRange type
type DateRange = { from: Date | undefined; to?: Date | undefined };

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  presets?: boolean; // show preset buttons
}

const presetOptions = [
  {
    label: 'Last 7 days',
    getRange: () => {
      const to = new Date();
      const from = subDays(to, 6);
      return { from, to };
    },
  },
  {
    label: 'Last 30 days',
    getRange: () => {
      const to = new Date();
      const from = subDays(to, 29);
      return { from, to };
    },
  },
  {
    label: 'This month',
    getRange: () => {
      const now = new Date();
      return { from: startOfMonth(now), to: endOfMonth(now) };
    },
  },
  {
    label: 'Custom',
    getRange: () => ({ from: undefined, to: undefined }),
  },
];

export function DateRangePicker({ value, onChange, presets = true }: DateRangePickerProps) {
  return (
    <div className="flex flex-col gap-2">
      {presets && (
        <div className="flex gap-2 mb-2">
          {presetOptions.map((preset) => (
            <button
              key={preset.label}
              type="button"
              className="px-3 py-1 rounded bg-muted text-xs hover:bg-accent border border-border"
              onClick={() => onChange(preset.getRange())}
            >
              {preset.label}
            </button>
          ))}
        </div>
      )}
      <DayPicker
        mode="range"
        selected={value}
        onSelect={onChange}
        showOutsideDays
        numberOfMonths={2}
        className="p-3 border rounded-lg bg-background"
      />
      <div className="flex justify-between text-xs mt-2">
        <span>
          {value.from ? value.from.toLocaleDateString() : 'Start'}
          {value.to ? ` → ${value.to.toLocaleDateString()}` : ''}
        </span>
        {(value.from || value.to) && (
          <button
            type="button"
            className="text-xs text-muted-foreground hover:underline"
            onClick={() => onChange({ from: undefined, to: undefined })}
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

const extendedPresets = [
  { label: 'Today', getRange: () => { const d = new Date(); return { from: d, to: d }; } },
  { label: 'Yesterday', getRange: () => { const d = new Date(); d.setDate(d.getDate() - 1); return { from: d, to: d }; } },
  { label: 'This week (Sun - Today)', getRange: () => { const d = new Date(); const from = new Date(d); from.setDate(d.getDate() - d.getDay()); return { from, to: d }; } },
  { label: 'Last week (Sun - Sat)', getRange: () => { const d = new Date(); const to = new Date(d); to.setDate(d.getDate() - d.getDay() - 1); const from = new Date(to); from.setDate(to.getDate() - 6); return { from, to }; } },
  { label: 'Last 7 days', getRange: () => { const to = new Date(); const from = new Date(); from.setDate(to.getDate() - 6); return { from, to }; } },
  { label: 'Last 28 days', getRange: () => { const to = new Date(); const from = new Date(); from.setDate(to.getDate() - 27); return { from, to }; } },
  { label: 'Last 30 days', getRange: () => { const to = new Date(); const from = new Date(); from.setDate(to.getDate() - 29); return { from, to }; } },
  { label: 'Last 90 days', getRange: () => { const to = new Date(); const from = new Date(); from.setDate(to.getDate() - 89); return { from, to }; } },
  { label: 'Last 12 months', getRange: () => { const to = new Date(); const from = new Date(); from.setFullYear(to.getFullYear() - 1); from.setDate(from.getDate() + 1); return { from, to }; } },
  { label: 'Custom', getRange: () => ({ from: undefined, to: undefined }) },
];

function formatRange(from?: Date, to?: Date) {
  if (!from || !to) return 'Select range';
  const opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
  return `${from.toLocaleDateString('en-GB', opts)} - ${to.toLocaleDateString('en-GB', opts)}`;
}

export function DateRangeDropdown({
  value,
  onChange,
  className,
}: {
  value: { from: Date | undefined; to?: Date | undefined };
  onChange: (range: { from: Date | undefined; to?: Date | undefined }) => void;
  className?: string;
}) {
  const [open, setOpen] = React.useState(false);

  // Format the button label
  const label = value.from && value.to
    ? `${format(value.from, "dd MMM yyyy")} - ${format(value.to, "dd MMM yyyy")}`
    : "Select date range";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`border rounded-md px-4 py-2 flex items-center gap-2 ${className ?? ""}`}
          type="button"
        >
          <span>{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="p-0">
        <Calendar
          mode="range"
          numberOfMonths={1}
          selected={value}
          onSelect={(range) => {
            onChange(range);
            if (range.from && range.to) setOpen(false);
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
